<template>
    <div class="header" :class="{ 'desktop-header': isDesktop, 'mobile-header': !isDesktop, 'workbench-header': isWorkbench, 'scrolled': isScrolled }">
        
        <div class="header-left">
            <div v-if="isWorkbench" class="workbench-brand-shell">
                <nuxt-link :to="localePath('/account?menu=home')" class="workbench-brand-lockup">
                    <img src="/assets/images/icon_logo.png" alt="Doclingo" class="workbench-brand-logo">
                </nuxt-link>
                <button
                    type="button"
                    class="workbench-region-switch"
                    :aria-expanded="regionSwitchOpen"
                    @click.stop="regionSwitchOpen = !regionSwitchOpen"
                >
                    <span>{{ currentRegionName }}</span>
                    <WorkbenchIcon name="chevron-down" :size="12" />
                </button>

                <div v-if="regionSwitchOpen" class="workbench-region-dropdown" @click.stop>
                    <button
                        type="button"
                        class="workbench-region-option"
                        :class="{ 'is-current': isVipSite }"
                        @click="handleRegionOption('vip')"
                    >
                        <WorkbenchIcon :name="isVipSite ? 'circle-check' : 'globe'" :size="13" />
                        <span class="workbench-region-option__text">
                            <strong>{{ $t('enterprise_management_china') }}<span v-if="isVipSite">{{ $t('enterprise_ui_region_current') }}</span></strong>
                            <small>{{ vipSiteDomain }}</small>
                        </span>
                        <WorkbenchIcon v-if="!isVipSite" name="external-link" :size="13" />
                    </button>
                    <button
                        type="button"
                        class="workbench-region-option"
                        :class="{ 'is-current': isAiSite }"
                        @click="handleRegionOption('ai')"
                    >
                        <WorkbenchIcon :name="isAiSite ? 'circle-check' : 'globe'" :size="13" />
                        <span class="workbench-region-option__text">
                            <strong>{{ $t('enterprise_management_international') }}<span v-if="isAiSite">{{ $t('enterprise_ui_region_current') }}</span></strong>
                            <small>{{ aiSiteDomain }}</small>
                        </span>
                        <WorkbenchIcon v-if="!isAiSite" name="external-link" :size="13" />
                    </button>
                    <div class="workbench-region-dropdown__divider" />
                    <p>{{ $t('enterprise_ui_region_switch_hint') }}</p>
                </div>
            </div>
            <nuxt-link v-else :to="isTranslate ? localePath('/account?menu=home') : isMaintenance ? localePath('/maintenance') : localePath('/')" class="logo">
                <CommonImage src="/assets/images/icon_logo.png" alt="logo" class="logo-img" :useElementUI="false" />
                <div class="point" />
                <div class="business-text">
                    {{ $t('header_business') }}
                </div>
            </nuxt-link>
            <div class="menu" v-if="isDesktop && !isWorkbench && !isTranslate && !isMaintenance">
                <div class="menu-item" v-for="(item, index) in menuList" :key="item.path">
                    <div class="menu-item-link" 
                         :class="{ 'active': activeSection === item.id }"
                         @click="scrollToSection(item.id)">
                        {{ $t(`${item.name}`) }}
                    </div>
                </div>
            </div>
        </div>

        <div class="header-right" v-if="!isMaintenance">
            <client-only>
                <nuxt-link
                    v-if="isWorkbench"
                    :to="localePath('/account?menu=support')"
                    class="workbench-support-link"
                >
                    <WorkbenchIcon name="headset" :size="14" />
                    <span>{{ t('enterprise_priority_support_title') }}</span>
                </nuxt-link>
                <!-- <el-popover placement="bottom" :width="100" trigger="click">
                    <template #reference>
                        <div class="concat-us">
                            <span class="iconfont icon-icon_customerservice_11"></span>
                        </div>
                    </template>
                </el-popover> -->
                <el-popover
                    placement="bottom"
                    popper-class="desktop-language-list"
                    :trigger="isDesktop ? 'hover' : 'click'"
                    :width="isDesktop ? 500 : 220"
                    ref="languagePopoverRef"
                >
                    <template #reference>
                        <div class="language">
                            <span class="iconfont icon-icon_earth_1"></span>
                            <span class="language-text">{{ systemLanguage?.name }}</span>
                        </div>
                    </template>
                    <div class="desktop-language-list-content">
                        <div class="language-list-item" v-for="item in languageList" :key="item.id"
                            @click="handleChangeLanguage(item)">
                            {{ item.name }}
                        </div>
                    </div>
                </el-popover>
                <div class="user-info">
                    <div v-if="!token" @click="showLogin" class="login-button" >{{t('loginAndRegister')}}</div>
                    <UserPopover v-else :avatar-size="40" />
                </div>
            </client-only>
        </div>
    </div>
</template>

<script setup lang="ts">
import useSystemLanguageStore from '~/store/modules/systemLaunage';
import type { SystemLanguageType } from '~/store/modules/systemLaunage';
import useUserStore from '~/store/modules/user';
import { SystemLanguageEnum, Token } from '~/types/const'
import UserPopover from './UserPopover/index.vue';
import { useCrossDomainCookie } from "@/composables/useCrossDomainCookie";
import { useI18n } from 'vue-i18n'
import eventBus from 'vue3-eventbus'
import CommonImage from '@/components/CommonImage.vue';
import WorkbenchIcon from '@/components/Workbench/WorkbenchIcon.vue';
import { useSiteConfig } from '~/config/useSiteConfig';
import Toast from '~/utils/toast'
const viewport = useViewport();
const isDesktop = computed(() => viewport.breakpoint?.value.includes('desktop'))
const userStore = useUserStore()

const { token, userInfo } = storeToRefs(userStore)

const systemLanguageStore = useSystemLanguageStore()
const { languageList, systemLanguage } = storeToRefs(systemLanguageStore)
const { t, locale, setLocale } = useI18n()
const localePath = useLocalePath()
const route = useRoute()
const { createCrossDomainCookie } = useCrossDomainCookie();

const props = defineProps({
    isWorkbench: {
        type: Boolean,
        default: false
    },
    isTranslate: {
        type: Boolean,
        default: false
    },
    isMaintenance: {
        type: Boolean,
        default: false
    }, // 维护中
})
// 当前激活的section
const activeSection = ref('product-view')
// 是否已滚动
const isScrolled = ref(false)
const languagePopoverRef = ref()
const regionSwitchOpen = ref(false)
const { isAiSite, isVipSite, counterpartSiteUrl } = useSiteConfig()
const { linkUrl } = useAppDomainConfig()
const currentRegionName = computed(() => t(isAiSite.value ? 'enterprise_management_international' : 'enterprise_management_china'))

const getSiteDomain = (url: string) => {
    try {
        return new URL(url).hostname
    } catch {
        return ''
    }
}

const vipSiteDomain = computed(() => getSiteDomain(isVipSite.value ? linkUrl.value : counterpartSiteUrl.value))
const aiSiteDomain = computed(() => getSiteDomain(isAiSite.value ? linkUrl.value : counterpartSiteUrl.value))

let menuList = ref([
    { name: 'header_products', path: '/', id: 'product-view' },
    { name: 'header_pro', path: '/', id: 'pro-view' },
    { name: 'header_case', path: '/', id: 'case-view' }
])

watch(locale, (newVal) => {
    handleChangeLanguage(languageList.value.find((item) => item.code === newVal) as SystemLanguageType)
}, {deep: true})

// 滚动到指定section
const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId)
    if (element) {
        element.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
        })
    }
}

// 监听滚动，更新active section和滚动状态
const handleScroll = () => {
    console.log('handleScroll')
    const layout = document.getElementById('main-layout-content')
    if (!layout) return
    const scrollPosition = layout.scrollTop
    isScrolled.value = scrollPosition > 50
    
    const sections = menuList.value.map(item => item.id)
    const adjustedScrollPosition = scrollPosition + 100 // 添加偏移量
    
    for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i])
        if (section && section.offsetTop <= adjustedScrollPosition) {
            activeSection.value = sections[i]
            break
        }
    }
}

const handleChangeLanguage = (item: SystemLanguageType) => {
    setLocale(item.code as SystemLanguageEnum)
    systemLanguageStore.setLanguage(item.code as SystemLanguageEnum)
    // useCookie('lang').value = item.code
    createCrossDomainCookie('lang', item.code)
    useHead({
        htmlAttrs: {
            lang: item.code
        }
    })
    languagePopoverRef.value?.hide()
}

const handleRegionOption = (edition: 'ai' | 'vip') => {
    const isCurrent = edition === (isAiSite.value ? 'ai' : 'vip')
    if (isCurrent) {
        regionSwitchOpen.value = false
        return
    }
    if (counterpartSiteUrl.value) {
        window.open(counterpartSiteUrl.value, '_blank', 'noopener,noreferrer')
    }
    regionSwitchOpen.value = false
}

const closeRegionSwitch = () => {
    regionSwitchOpen.value = false
}
const { $loginCom } = useNuxtApp();
const showLogin = ()=>{
    console.log('showLogin', useNuxtApp())
    if (!isDesktop.value) {
        Toast(t('go_web_version'))
        return
    }
    $loginCom.open();
}

onMounted(() => {
    document.addEventListener('click', closeRegionSwitch)
    eventBus.on('openWorkbenchRegionSwitch', () => {
        regionSwitchOpen.value = true
    })
    let layout = document.getElementById('main-layout-content')
    layout?.addEventListener('scroll', handleScroll)
    // 初始化时检查当前section和滚动状态
    handleScroll()

    eventBus.on('switchLanguage', (lang: SystemLanguageEnum) => {
        setTimeout(()=>{
            console.log('switchLanguage', lang, languageList.value)
            handleChangeLanguage(languageList.value.find((item) => item.code === lang) as SystemLanguageType)
        }, 2000)
    })
})

onUnmounted(() => {
    document.removeEventListener('click', closeRegionSwitch)
    eventBus.off('openWorkbenchRegionSwitch')
    let layout = document.getElementById('main-layout-content')
    layout?.removeEventListener('scroll', handleScroll)
    eventBus.off('switchLanguage')
})
</script>

<style lang="scss" scoped>
@use './index.scss' as *;
</style>    
